import { useCallback, useMemo } from 'react';

import {
  OrganizationDTOStatus,
  useActivateOrganization,
  useDeactivateOrganization,
  useHoldOrganization,
  useResumeOrganization
} from '@/api/generated';

import { useOrganizationPermissions } from './permissions/useOrganizationPermissions';

enum OrganizationStatusActions {
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  RESUME = 'RESUME',
  HOLD = 'HOLD'
}

interface OrganizationStatusTransition {
  from: OrganizationDTOStatus;
  to: OrganizationDTOStatus;
  name: OrganizationStatusActions;
  handler: () => Promise<void>;
}

export const useOrganizationStatusActions = (options: {
  organizationSlug: string;
  currentStatus?: OrganizationDTOStatus;
  onChange: (newStatus: OrganizationDTOStatus) => void;
}) => {
  const { organizationSlug, currentStatus, onChange } = options;

  const { mutateAsync: activateOrganization, isLoading: isActivateOrgLoading } = useActivateOrganization();
  const { mutateAsync: deactivateOrganization, isLoading: isDeactivateOrgLoading } = useDeactivateOrganization();
  const { mutateAsync: resumeOrganization, isLoading: isResumeOrgLoading } = useResumeOrganization();
  const { mutateAsync: holdOrganization, isLoading: isHoldOrgLoading } = useHoldOrganization();

  const { value: organizationPermissions } = useOrganizationPermissions({
    variables: { organizationSlug }
  });

  const isStatusLoading = isActivateOrgLoading || isDeactivateOrgLoading || isResumeOrgLoading || isHoldOrgLoading;

  const statusTransitions = useMemo(
    () =>
      [
        (!organizationPermissions || organizationPermissions.canActivate) && {
          from: OrganizationDTOStatus.DRAFT,
          to: OrganizationDTOStatus.ACTIVE,
          name: OrganizationStatusActions.ACTIVATE,
          handler: () => activateOrganization({ organizationSlug })
        },
        (!organizationPermissions || organizationPermissions.canActivate) && {
          from: OrganizationDTOStatus.INACTIVE,
          to: OrganizationDTOStatus.ACTIVE,
          name: OrganizationStatusActions.ACTIVATE,
          handler: () => activateOrganization({ organizationSlug })
        },
        (!organizationPermissions || organizationPermissions.canDeactivate) && {
          from: OrganizationDTOStatus.ACTIVE,
          to: OrganizationDTOStatus.INACTIVE,
          name: OrganizationStatusActions.DEACTIVATE,
          handler: () => deactivateOrganization({ organizationSlug })
        },
        (!organizationPermissions || organizationPermissions.canHold) && {
          from: OrganizationDTOStatus.ACTIVE,
          to: OrganizationDTOStatus.ON_HOLD,
          name: OrganizationStatusActions.HOLD,
          handler: () => holdOrganization({ organizationSlug })
        },
        (!organizationPermissions || organizationPermissions.canActivate) && {
          from: OrganizationDTOStatus.ON_HOLD,
          to: OrganizationDTOStatus.ACTIVE,
          name: OrganizationStatusActions.RESUME,
          handler: () => resumeOrganization({ organizationSlug })
        }
      ].filter(Boolean) as OrganizationStatusTransition[],
    [
      organizationPermissions,
      activateOrganization,
      organizationSlug,
      deactivateOrganization,
      holdOrganization,
      resumeOrganization
    ]
  );

  const availableTransitions = useMemo(
    () => statusTransitions.filter(({ from }) => from === currentStatus),
    [currentStatus, statusTransitions]
  );
  const availableStatuses = useMemo(
    () => [currentStatus, ...availableTransitions.map(({ to }) => to)],
    [availableTransitions, currentStatus]
  );
  const isFinalState = useMemo(() => !availableTransitions.length, [availableTransitions]);
  const availableActions = useMemo(() => availableTransitions.map(({ name }) => name), [availableTransitions]);

  const isActivateAvailable = useMemo(
    () => availableActions.includes(OrganizationStatusActions.ACTIVATE),
    [availableActions]
  );
  const isDeactivateAvailable = useMemo(
    () => availableActions.includes(OrganizationStatusActions.DEACTIVATE),
    [availableActions]
  );
  const isHoldAvailable = useMemo(() => availableActions.includes(OrganizationStatusActions.HOLD), [availableActions]);
  const isResumeAvailable = useMemo(
    () => availableActions.includes(OrganizationStatusActions.RESUME),
    [availableActions]
  );

  const handleStatusChange = useCallback(
    async (newStatus: OrganizationDTOStatus) => {
      const originalStatus = currentStatus;
      try {
        const transition = availableTransitions.find(({ from, to }) => originalStatus === from && newStatus === to);
        if (!transition) {
          throw new Error(`Transition impossible: ${originalStatus} => ${newStatus}`);
        }
        onChange(newStatus);
        if (transition.handler) {
          await transition.handler();
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Transition is not implemented: ${transition.name}`);
        }
      } catch (e) {
        // TODO: proper logging
        console.error(e); // eslint-disable-line no-console
        onChange(originalStatus!);
      }
    },
    [availableTransitions, currentStatus, onChange]
  );

  const handleActivate = useCallback(() => {
    if (isActivateAvailable) {
      handleStatusChange(OrganizationDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isActivateAvailable]);

  const handleDeactivate = useCallback(() => {
    if (isDeactivateAvailable) {
      handleStatusChange(OrganizationDTOStatus.INACTIVE);
    }
  }, [handleStatusChange, isDeactivateAvailable]);

  const handleHold = useCallback(() => {
    if (isHoldAvailable) {
      handleStatusChange(OrganizationDTOStatus.ON_HOLD);
    }
  }, [handleStatusChange, isHoldAvailable]);

  const handleResume = useCallback(() => {
    if (isResumeAvailable) {
      handleStatusChange(OrganizationDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isResumeAvailable]);

  return {
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses,
    activate: handleActivate,
    deactivate: handleDeactivate,
    hold: handleHold,
    resume: handleResume,
    change: handleStatusChange,
    isActivateAvailable,
    isDeactivateAvailable,
    isHoldAvailable,
    isResumeAvailable
  };
};
